import './css/CommitCanvas.css';

import target from '../assets/images/tool_target.png';

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import CustomHeatMap from './CustomHeatMap.jsx';
import CommitTools from './CommitTools.jsx';

import {makeCommits, testy} from '../services/github-api.js'
import { generateNoise } from '../utils/noise.js';

const CommitCanvas = ({ contributions, username, token, userRepos, starredRepos, setStarredRepos }) => {
    const [zoom, setZoom] = useState(0.8);
    const [translate, setTranslate] = useState([0, 0]);
    const [mouseDown, setMouseDown] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);

    const [selectedYear, setSelectedYear] = useState(() => {
        if (!contributions || typeof contributions !== "object") {
            return new Date().getFullYear().toString();
        }
        const availableYears = Object.keys(contributions).sort();
        return availableYears[availableYears.length - 1] || new Date().getFullYear().toString();
    });

    const [selectedLevel, setSelectedLevel] = useState(2);

    const [currentSquare, setCurrentSquare] = useState(null);
    const [circleSize, setCircleSize] = useState(10);
    const [selectedTool, setSelectedTool] = useState(1);

    const [commitReady, setCommitReady] = useState(false);

    const [newCommits, setNewCommits] = useState({});
    const [formattedCommits, setFormattedCommits] = useState({})

    const [selectedRepoData, setSelectedRepoData] = useState(null) // State for selected repository data


    const [showRepoDropdown, setShowRepoDropdown] = useState(false);

    const availableYears = useMemo(() => {
        if (!contributions || typeof contributions !== "object") {
            return [];
        }
        return Object.keys(contributions).sort();
    }, [contributions]);

    const canvasRef = useRef(null);
    const viewControlsRef = useRef(null);
    const animationFrameRef = useRef(null);
    const drawCursorRef = useRef(null);

    const handleSubmitCommits = async () => {
        if (!commitReady) return {status: false}
        try {
            console.log('Committing...')
            // console.log(token, username, 'automatic-carnival', formattedCommits)
            // console.log(selectedRepoData)
            // makeCommits(token, username, 'automatic-carnival', formattedCommits)
            if (selectedRepoData && selectedRepoData.name) {
                const data = await makeCommits(token, username, selectedRepoData.name, formattedCommits)
                return data
            }
        } catch (e){
            console.error(e)
            return {status: false}
        }
        return {status: false}
    }
    
    const updateTransform = useCallback(() => {
        const viewControls = viewControlsRef.current;
        if (viewControls) {
            viewControls.style.transform = `scale(${zoom}) translate(${translate[0]}px, ${translate[1]}px)`;
        }
    }, [zoom, translate]);

    const clearCanvas = () => {
        // add a popup modal to confirm
        setNewCommits({})
    }

    const recenterCanvas = () => {
        setZoom(0.8);
        setTranslate([0, 0]);
    };

    const getContributionColor = (level) => {
        if (level === 0) return 'transparent';
        const minColor = { r: 14, g: 68, b: 41 };   // #0e4429
        const maxColor = { r: 57, g: 211, b: 83 };  // #39d353
        const ratio = level / 10;
        const r = Math.round(minColor.r + (maxColor.r - minColor.r) * ratio);
        const g = Math.round(minColor.g + (maxColor.g - minColor.g) * ratio);
        const b = Math.round(minColor.b + (maxColor.b - minColor.b) * ratio);
        return `rgb(${r}, ${g}, ${b})`;
    };

    const handleShuffle = (maxIntensity) => {
        setNewCommits(prev => {
            const updatedCommits = { ...prev };
            if (!updatedCommits[selectedYear]) {
                updatedCommits[selectedYear] = {};
            }

            // Get all dates for the selected year
            // We can iterate through the existing contributions or generate dates for the year
            // Since we want to fill the year, let's iterate through all days in the year
            
            const startDate = new Date(`${selectedYear}-01-01`);
            const endDate = new Date(`${selectedYear}-12-31`);
            
            // Use a random seed offset for each shuffle
            const seed = Math.random() * 1000;

            for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                const dateStr = d.toISOString().split('T')[0];
                
                // Calculate noise value based on day of year
                const dayOfYear = Math.floor((d - new Date(d.getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24);
                
                // Scale input for noise to get nice "clouds"
                // Increased frequency to 0.2 for more randomness in chunks
                const noiseValue = generateNoise((dayOfYear * 0.2) + seed);
                
                // Map noise (0-1) to commit count (0-maxIntensity)
                // Increased threshold to 0.94 so at least half are blank (mean is ~0.94)
                let commitCount = 0;
                
                if (noiseValue > 0.94) {
                    const normalized = (noiseValue - 0.94) / 0.935; // 0 to 1 (approx max 1.875)
                    commitCount = Math.ceil(normalized * maxIntensity);
                }

                if (commitCount > 0) {
                    updatedCommits[selectedYear][dateStr] = {
                        contributionCount: commitCount,
                        level: commitCount, // Assuming level maps 1:1 to count for now, or cap at 10
                        date: dateStr,
                        color: getContributionColor(Math.min(commitCount, 10))
                    };
                } else {
                    // If we want to clear existing commits on shuffle for days that become empty:
                    // delete updatedCommits[selectedYear][dateStr];
                    // Or keep them? Usually shuffle implies a full reset/overwrite.
                    // Let's overwrite/clear.
                    if (updatedCommits[selectedYear][dateStr]) {
                        delete updatedCommits[selectedYear][dateStr];
                    }
                }
            }
            
            return updatedCommits;
        });
    };

    useEffect(() => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }
        animationFrameRef.current = requestAnimationFrame(() => {
            updateTransform();
        });

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [updateTransform]);

    useEffect(() => {
        
        const handleMouseDown = (e) => {
            setMouseDown(true);
        }

        const handleMouseUp = () => {
            setMouseDown(false);
        }

        const handleMouseMove = (e) => {
            const drawCursor = drawCursorRef.current;
            if (drawCursor) {
                drawCursor.style.left = `${e.pageX}px`;
                drawCursor.style.top = `${e.pageY}px`;
            }

            if (mouseDown) {
                const drawCursor = drawCursorRef.current;
                if (!drawCursor) return;

                const cursorRect = drawCursor.getBoundingClientRect();
                const squareElements = document.querySelectorAll('.square');

                const closestSquares = Array.from(squareElements).filter((square) => {
                    const rect = square.getBoundingClientRect();
                    return !(
                        cursorRect.right < rect.left ||
                        cursorRect.left > rect.right ||
                        cursorRect.bottom < rect.top ||
                        cursorRect.top > rect.bottom
                    );
                });
                
                closestSquares.forEach((closestSquare) => {
                    if (closestSquare && closestSquare !== currentSquare) {
                        setCurrentSquare(closestSquare);
                        const innerDiv = closestSquare.querySelector('.inner');
                        const date = closestSquare.getAttribute('date');
                        const count = closestSquare.getAttribute('count');
                        const original = closestSquare.getAttribute('original');
                        
                        // TOOL 1: Pen (add commit)
                        if (selectedTool === 1) {
                            setNewCommits(prev => {
                                const newCommits = { ...prev };
                                if (!newCommits[selectedYear]) {
                                    newCommits[selectedYear] = {};
                                }
                                if (!newCommits[selectedYear][date]) {
                                    newCommits[selectedYear][date] = {
                                        contributionCount: 0,
                                        level: selectedLevel,
                                        date: date,
                                        color: innerDiv.style.backgroundColor
                                    };
                                }
                                newCommits[selectedYear][date].level = selectedLevel;
                                newCommits[selectedYear][date].contributionCount = selectedLevel;
                                return newCommits;
                            });
                        }

                        // TOOL 2: Eraser (remove commit)
                        if (selectedTool === 2) {
                            setNewCommits(prev => {
                                const newCommits = { ...prev };
                                if (
                                    newCommits[selectedYear] &&
                                    Object.prototype.hasOwnProperty.call(newCommits[selectedYear], date)
                                ) {
                                    newCommits[selectedYear] = { ...newCommits[selectedYear] };
                                    delete newCommits[selectedYear][date];
                                    if (Object.keys(newCommits[selectedYear]).length === 0) {
                                        delete newCommits[selectedYear];
                                    }
                                }
                                return newCommits;
                            });
                        }
                    }
                });
            }

        };

        const handleWheel = (e) => {
            if (showRepoDropdown) return; // Disable canvas zoom/pan when dropdown is open

            e.preventDefault();
            
            const sx = e.deltaX;
            const sy = e.deltaY;
            const ctrl = e.ctrlKey || e.metaKey;
            const shift = e.shiftKey;

            setIsTransitioning(false);

            if (ctrl) {
                const canvas = canvasRef.current;
                if (!canvas) return;

                const rect = canvas.getBoundingClientRect();
                const mouseX = e.clientX - rect.left;
                const mouseY = e.clientY - rect.top;

                setZoom(prevZoom => {
                    let newZoom = prevZoom - (sy * 0.002);
                    newZoom = Math.max(0.5, Math.min(10, newZoom));

                    const scale = newZoom / prevZoom;

                    setTranslate(prevTranslate => {
                        const [tx, ty] = prevTranslate;
                        const pointX = (mouseX - tx) / prevZoom;
                        const pointY = (mouseY - ty) / prevZoom;
                        const newTx = mouseX - pointX * newZoom;
                        const newTy = mouseY - pointY * newZoom;
                        return [newTx, newTy];
                    });

                    return newZoom;
                });
            } else {
                setTranslate(prev => {
                    const sensitivity = 0.8;
                    let deltaX = sx * sensitivity;
                    let deltaY = shift ? 0 : sy * sensitivity;
                    if (shift) deltaX = (sx || sy) * sensitivity;
                    return [prev[0] - deltaX, prev[1] - deltaY];
                });
            }
        }

        const handleKeyDown = (e) => {
            if (e.key === 'r') {
                recenterCanvas()
            }
        }

        const canvas = canvasRef.current;
        if (!canvas) return;

        canvas.addEventListener('wheel', handleWheel, { passive: false });
        canvas.addEventListener('mousedown', handleMouseDown);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('keydown', handleKeyDown)

        return () => {
            canvas.removeEventListener('wheel', handleWheel);
            canvas.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('mousemove', handleMouseMove);
        };
    // }, [handleWheel, handleKeyDown, handleMouseDown, handleMouseUp, mouseDown, selectedTool]);
    });

    useEffect(() => {
        return () => {
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    
    useEffect(() => {
        if (newCommits[selectedYear]) {
            if (Object.keys(newCommits[selectedYear]).length > 0) setCommitReady(true);
            else setCommitReady(false);
        } else setCommitReady(false);
    },[newCommits, selectedYear])

    useEffect(()=>{
        const formatContributions = (input) => {
            const result = [];
            if (!input) return result

            Object.values(input).forEach(entry => {
                const [yearStr, monthStr, dayStr] = entry.date.split("-");
                const year = parseInt(yearStr);
                const month = parseInt(monthStr); // 1-based (January = 1)
                const day = parseInt(dayStr);
                const level = entry.level

                for (let i = 0; i < entry.contributionCount; i++) {
                    const content = `Commit ${i + 1} of ${entry.contributionCount}: ${entry.date}`;

                    result.push({
                        content,
                        level,
                        year,
                        month,
                        day
                    });
                }
            });

            return result;
        }
        const response = formatContributions(newCommits?.[selectedYear])
        setFormattedCommits(response)

    }, [newCommits])

    return (
        <div className={`commit-canvas`} ref={canvasRef}>
            {(selectedTool === 1 || selectedTool === 2) && (
                <div
                    className="draw-cursor"
                    ref={drawCursorRef}
                    style={{ width: `${circleSize}px`, height: `${circleSize}px` }}
                ></div>
            )}
            <div
                className={`view-controls ${isTransitioning ? 'transitioning' : ''}`}
                ref={viewControlsRef}
            >
                <CustomHeatMap 
                    contributions={contributions} 
                    selectedYear={selectedYear}
                    selectedTool={selectedTool}
                    newCommits={newCommits}
                    setNewCommits={setNewCommits}
                />
            </div>

            <div className="canvas-recenter" onClick={recenterCanvas}>
                <img src={target} alt="" />
            </div>

            <CommitTools 
                availableYears={availableYears}
                selectedYear={selectedYear}
                onYearChange={setSelectedYear}
                selectedTool={selectedTool}
                setSelectedTool={setSelectedTool}
                selectedLevel={selectedLevel}
                setSelectedLevel={setSelectedLevel}
                circleSize={circleSize}
                setCircleSize={setCircleSize}
                recenterCanvas={recenterCanvas}
                commitReady={commitReady}
                handleSubmitCommits={handleSubmitCommits}
                clearCanvas={clearCanvas}
                formattedCommits={formattedCommits}
                userRepos={userRepos}
                setSelectedRepoData={setSelectedRepoData}
                handleShuffle={handleShuffle}
                starredRepos={starredRepos}
                setStarredRepos={setStarredRepos}
                showRepoDropdown={showRepoDropdown}
                setShowRepoDropdown={setShowRepoDropdown}
            />
        </div>
    );
};

export default CommitCanvas;
